import React from 'react';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Stack,
    Divider,
} from '@mui/material';
import { useShallow } from 'zustand/react/shallow';
import { useBayStore } from '../../Stores/BayStore';
import { BayData } from '../../Types/InspectionTypes';
import DeltaTSection from './DeltaTSection';
import SproutingSection from './SproutingSection';
import BreakdownSection from './BreakdownSection';

interface BaySectionProps {
    bayIndex: number;
    isMobile?: boolean;
}

const BaySection: React.FC<BaySectionProps> = ({
    bayIndex,
    isMobile = false,
}) => {
    const bay = useBayStore((state) => state.bays[bayIndex]);
    const { updateBayField, updateBaySprouting, updateBayBreakdown } = useBayStore(
        useShallow((state) => ({
            updateBayField: state.updateBayField,
            updateBaySprouting: state.updateBaySprouting,
            updateBayBreakdown: state.updateBayBreakdown,
        }))
    );

    if (!bay) {
        return null;
    }

    const handleFieldUpdate = <K extends keyof BayData>(field: K, value: BayData[K]) => {
        updateBayField(bayIndex, field, value);
    };

    return (
        <Paper 
            elevation={isMobile ? 1 : 3} 
            sx={{ 
                p: isMobile ? 2 : 3, 
                mb: isMobile ? 3 : 3, 
                backgroundColor: 'background.paper',
                border: isMobile ? '1px solid' : 'none',
                borderColor: 'divider'
            }}
        >
            <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                sx={{ mb: isMobile ? 1 : 2, color: 'primary.main', fontWeight: 600 }}
            >
                Bay: {bay.bay}
            </Typography>

            {/* Delta T Sections */}
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
                <Typography 
                    variant={isMobile ? "body2" : "subtitle1"} 
                    sx={{ mb: isMobile ? 1 : 2, fontWeight: 600, color: 'text.primary' }}
                >
                    Temperature Measurements (°F)
                </Typography>
                
                <DeltaTSection
                    label="Front"
                    topValue={bay.topFront}
                    bottomValue={bay.bottomFront}
                    avgDeltaT={bay.frontAvgDeltaT}
                    onTopChange={(val) => handleFieldUpdate('topFront', val)}
                    onBottomChange={(val) => handleFieldUpdate('bottomFront', val)}
                    isMobile={isMobile}
                />
                
                <DeltaTSection
                    label="Middle"
                    topValue={bay.topMiddle}
                    bottomValue={bay.bottomMiddle}
                    avgDeltaT={bay.middleAvgDeltaT}
                    onTopChange={(val) => handleFieldUpdate('topMiddle', val)}
                    onBottomChange={(val) => handleFieldUpdate('bottomMiddle', val)}
                    isMobile={isMobile}
                />
                
                <DeltaTSection
                    label="Back"
                    topValue={bay.topBack}
                    bottomValue={bay.bottomBack}
                    avgDeltaT={bay.backAvgDeltaT}
                    onTopChange={(val) => handleFieldUpdate('topBack', val)}
                    onBottomChange={(val) => handleFieldUpdate('bottomBack', val)}
                    isMobile={isMobile}
                />

                {/* Average Delta T Display */}
                <Box sx={{ mt: isMobile ? 1 : 2, p: isMobile ? 1.5 : 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
                    <Stack direction={isMobile ? "column" : "row"} spacing={isMobile ? 1 : 3} justifyContent="space-around">
                        <Box sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Average Delta T
                            </Typography>
                            <Typography variant={isMobile ? "body1" : "h6"} color="primary.dark" sx={{ fontWeight: 600 }}>
                                {bay.averageDeltaT.toFixed(2)}°F
                            </Typography>
                        </Box>
                        <Box sx={{ textAlign: isMobile ? 'left' : 'center' }}>
                            <Typography variant="caption" display="block" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                Avg SP Delta T
                            </Typography>
                            <Typography variant={isMobile ? "body1" : "h6"} color="secondary.dark" sx={{ fontWeight: 600 }}>
                                {bay.avgSpDeltaT.toFixed(2)}°F
                            </Typography>
                        </Box>
                    </Stack>
                </Box>
            </Box>

            <Divider sx={{ my: isMobile ? 1.5 : 3 }} />

            {/* Condition Dropdowns */}
            <Box sx={{ mb: isMobile ? 2 : 3 }}>
                <Typography 
                    variant={isMobile ? "body2" : "subtitle1"} 
                    sx={{ mb: isMobile ? 1 : 2, fontWeight: 600, color: 'text.primary' }}
                >
                    Conditions
                </Typography>
                <Stack direction={isMobile ? "column" : "row"} spacing={2} sx={{ mb: 2 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Odors</InputLabel>
                        <Select
                            value={bay.odors}
                            label="Odors"
                            onChange={(e) => handleFieldUpdate('odors', e.target.value as BayData['odors'])}
                        >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="light">Light</MenuItem>
                            <MenuItem value="moderate">Moderate</MenuItem>
                            <MenuItem value="excessive">Excessive</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>Bugs</InputLabel>
                        <Select
                            value={bay.bugs}
                            label="Bugs"
                            onChange={(e) => handleFieldUpdate('bugs', e.target.value as BayData['bugs'])}
                        >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="light">Light</MenuItem>
                            <MenuItem value="moderate">Moderate</MenuItem>
                            <MenuItem value="excessive">Excessive</MenuItem>
                        </Select>
                    </FormControl>

                    <FormControl fullWidth size="small">
                        <InputLabel>Duct Liquid</InputLabel>
                        <Select
                            value={bay.ductLiquid}
                            label="Duct Liquid"
                            onChange={(e) => handleFieldUpdate('ductLiquid', e.target.value as BayData['ductLiquid'])}
                        >
                            <MenuItem value="none">None</MenuItem>
                            <MenuItem value="light">Light</MenuItem>
                            <MenuItem value="moderate">Moderate</MenuItem>
                            <MenuItem value="excessive">Excessive</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>

                <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Current Fill Level (%)"
                        type="number"
                        value={bay.currentFillLevel}
                        onChange={(e) => handleFieldUpdate('currentFillLevel', e.target.value === '' ? '' : parseFloat(e.target.value))}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                    />

                    <FormControl fullWidth size="small">
                        <InputLabel>Top Pile Conditions</InputLabel>
                        <Select
                            value={bay.topPileConditions}
                            label="Top Pile Conditions"
                            onChange={(e) => handleFieldUpdate('topPileConditions', e.target.value as BayData['topPileConditions'])}
                        >
                            <MenuItem value="Dry">Dry</MenuItem>
                            <MenuItem value="Condensation">Condensation</MenuItem>
                            <MenuItem value="Damp">Damp</MenuItem>
                            <MenuItem value="Wet">Wet</MenuItem>
                        </Select>
                    </FormControl>
                </Stack>
            </Box>

            <Divider sx={{ my: isMobile ? 1.5 : 3 }} />

            {/* Sprouting Section */}
            <SproutingSection
                sprouting={bay.sprouting}
                onUpdate={(field, value) => updateBaySprouting(bayIndex, field, value)}
                isMobile={isMobile}
            />

            {/* Breakdown Section */}
            <BreakdownSection
                breakdown={bay.breakdown}
                onUpdate={(field, value) => updateBayBreakdown(bayIndex, field, value)}
                isMobile={isMobile}
            />

            {/* Notes */}
            <TextField
                fullWidth
                multiline
                rows={isMobile ? 2 : 3}
                label="Bay Notes"
                value={bay.notes}
                onChange={(e) => handleFieldUpdate('notes', e.target.value)}
                placeholder="Enter any additional notes for this bay..."
                size="small"
            />
        </Paper>
    );
};

export default BaySection;
